/**
 * Contrato de eventos del cliente de Staff (T018/T019).
 *
 * `src/api/client.ts` (T018) emite estos eventos sobre `window` cuando la API responde con los
 * códigos de `contracts/staff-api.md`; `routes/__root.tsx` los escucha y navega a la pantalla de
 * acceso correspondiente. Ningún otro módulo debe usar los literales: siempre `FERMIN_EVENTS`.
 *
 * | evento              | origen (código de la API)                          | destino en el root            |
 * |---------------------|----------------------------------------------------|-------------------------------|
 * | `unauthenticated`   | 401 `UNAUTHENTICATED`                              | `/dispositivo-no-registrado`  |
 * | `deviceMismatch`    | 401 `DEVICE_MISMATCH` o `DEVICE_CLOCK_SKEW` (código en `detail.code`)   | `/dispositivo-no-registrado`  |
 * | `workerInactive`    | 403 `WORKER_INACTIVE`                              | `/cuenta-inactiva`            |
 * | `companySuspended`  | 423 `COMPANY_SUSPENDED`                            | `/servicio-suspendido`        |
 * | `privacyRequired`   | 428 `PRIVACY_NOTICE_REQUIRED`                      | flag en `privacy-store.ts`    |
 */
import type { ErrorCode } from '@fermin/contracts';

export const FERMIN_EVENTS = {
  unauthenticated: 'fermin:unauthenticated',
  deviceMismatch: 'fermin:device-mismatch',
  workerInactive: 'fermin:worker-inactive',
  companySuspended: 'fermin:company-suspended',
  privacyRequired: 'fermin:privacy-required',
} as const;

export type FerminEventKey = keyof typeof FERMIN_EVENTS;
export type FerminEventName = (typeof FERMIN_EVENTS)[FerminEventKey];

/** Detalle opcional: el sobre de error que originó el evento (nunca tokens ni cabeceras). */
export interface FerminEventDetail {
  code?: ErrorCode;
  message?: string;
  status?: number;
  requestId?: string;
}

export type FerminEventHandler = (
  detail: FerminEventDetail | undefined,
  event: CustomEvent<FerminEventDetail | undefined>,
) => void;

/** Emite el evento sobre `window`. No hace nada fuera del navegador. */
export function emitFerminEvent(name: FerminEventName, detail?: FerminEventDetail): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent<FerminEventDetail | undefined>(name, { detail }));
}

/** Suscribe un manejador y devuelve la función para desuscribirse. */
export function onFerminEvent(name: FerminEventName, handler: FerminEventHandler): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const listener = (event: Event) => {
    const custom = event as CustomEvent<FerminEventDetail | undefined>;
    handler(custom.detail, custom);
  };
  window.addEventListener(name, listener);
  return () => window.removeEventListener(name, listener);
}
